// src/components/webpage/PromoBanner.jsx
import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";

/**
 * Promo banner (frontend):
 * - Banner with message
 * - Countdown + CTA button on the RIGHT (together)
 * - Close X pinned to TOP-RIGHT of the whole banner
 * - CTA opens modal (email + RODO)
 * - API call:
 *    POST `${API_BASE}/api/promo/send-code`
 * - Hide on scroll (optional)
 * - Close banner persisted via localStorage
 *
 * ✅ Updates requested:
 * - Removed "-20%" everywhere (badge, copy, CTA text)
 * - More radical, instantly visible banner color
 */

// API base (same pattern as in your pages)
const API_BASE =
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.trim()) ||
  "https://server229256.nazwa.pl";

async function fetchJSON(url, options) {
  const res = await fetch(url, options);
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    const text = await res.text();
    console.error("Non-JSON response:", res.status, text);
    throw new Error(`Unexpected response ${res.status} (see console)`);
  }
  const data = await res.json();
  if (!res.ok) {
    throw new Error((data && data.error) || `HTTP ${res.status}`);
  }
  return data;
}

const PromoBanner = ({
  endsAt = null,

  // Hide on scroll
  hideOnScroll = true,
  hideAfterPx = 60,
  showAgainAtTop = true,

  // Close button persistence
  dismissKey = "promoBannerDismissed",
}) => {
const targetDate = useMemo(() => {
  if (endsAt) return new Date(endsAt);

  // Default: end of current March if we're in March, otherwise next March.
  const now = new Date();
  const year = now.getMonth() === 2 ? now.getFullYear() : now.getFullYear() + 1;
  const lastDayOfMar = new Date(year, 3, 0); // Apr 0 => last day Mar
  lastDayOfMar.setHours(23, 59, 59, 999);
  return lastDayOfMar;
}, [endsAt]);


  const [timeLeft, setTimeLeft] = useState(getTimeLeft(targetDate));

  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);

  const [status, setStatus] = useState({ type: "idle", message: "" }); // idle | success | error | info
  const [loading, setLoading] = useState(false);

  const [hiddenByScroll, setHiddenByScroll] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);

  // Restore dismissed state
  useEffect(() => {
    if (!dismissKey) return;
    try {
      const v = localStorage.getItem(dismissKey);
      if (v === "1") setDismissed(true);
    } catch (_) {}
  }, [dismissKey]);

  // Countdown tick
  useEffect(() => {
    const t = setInterval(() => setTimeLeft(getTimeLeft(targetDate)), 1000);
    return () => clearInterval(t);
  }, [targetDate]);

  // Hide on scroll (rAF throttle)
  useEffect(() => {
    if (!hideOnScroll) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        if (y > hideAfterPx) setHiddenByScroll(true);
        else if (showAgainAtTop) setHiddenByScroll(false);
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, [hideOnScroll, hideAfterPx, showAgainAtTop]);

  // Close modal on ESC
  useEffect(() => {
    if (!isModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isModalOpen]);

  const expired = timeLeft.totalMs <= 0;

  const onCloseBanner = () => {
    setDismissed(true);
    if (dismissKey) {
      try {
        localStorage.setItem(dismissKey, "1");
      } catch (_) {}
    }
  };

  const onOpenModal = () => {
    if (expired) {
      setStatus({ type: "error", message: "Promocja zakończona." });
      return;
    }
    setStatus({ type: "idle", message: "" });
    setModalOpen(true);
  };

  const onCloseModal = () => {
    if (loading) return;
    setModalOpen(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "idle", message: "" });

    if (expired) {
      setStatus({ type: "error", message: "Promocja zakończona." });
      return;
    }

    const trimmed = email.trim().toLowerCase();

    if (!isValidEmail(trimmed)) {
      setStatus({ type: "error", message: "Podaj poprawny adres e-mail." });
      return;
    }

    if (!consent) {
      setStatus({ type: "error", message: "Aby otrzymać kod, zaznacz zgodę RODO." });
      return;
    }

    try {
      setLoading(true);

      await sendDiscountCode({ email: trimmed, consent });

      setStatus({
        type: "success",
        message: "Kod promocyjny został wysłany na Twój adres e-mail. Sprawdź skrzynkę.",
      });

      setEmail("");
      setConsent(false);

      setTimeout(() => setModalOpen(false), 900);
    } catch (err) {
      const code = err?.message || "SEND_FAILED";
      setStatus({ type: "error", message: mapErrorToMessage(code) });
    } finally {
      setLoading(false);
    }
  };

  if (dismissed) return null;
  if (hiddenByScroll) return null;

  return (
    <>
      <Wrap role="region" aria-label="Oferta startowa">
        {/* pinned top-right */}
        <CloseButton type="button" aria-label="Zamknij banner" onClick={onCloseBanner}>
          ×
        </CloseButton>

        <Inner>
          <Left>
<Title>
  Oferta startowa: <strong>uruchom wszystkie plany</strong>.
  <Muted> Tylko do końca marca.</Muted>
</Title>

            {status.type === "error" && (
              <InlineStatus role="status" aria-live="polite">
                {status.message}
              </InlineStatus>
            )}

            {expired && <Expired>Promocja zakończona.</Expired>}
          </Left>

          <Right>
            <RightRow>
              <Countdown aria-label="Odliczanie do końca promocji">
                <TimeBox>
                  <TimeValue>{pad(timeLeft.days)}</TimeValue>
                  <TimeLabel>d</TimeLabel>
                </TimeBox>
                <Sep>:</Sep>
                <TimeBox>
                  <TimeValue>{pad(timeLeft.hours)}</TimeValue>
                  <TimeLabel>h</TimeLabel>
                </TimeBox>
                <Sep>:</Sep>
                <TimeBox>
                  <TimeValue>{pad(timeLeft.minutes)}</TimeValue>
                  <TimeLabel>m</TimeLabel>
                </TimeBox>
                <Sep>:</Sep>
                <TimeBox>
                  <TimeValue>{pad(timeLeft.seconds)}</TimeValue>
                  <TimeLabel>s</TimeLabel>
                </TimeBox>
              </Countdown>

              <GetCodeButton type="button" onClick={onOpenModal} disabled={expired}>
                Odbierz kod - 20%
              </GetCodeButton>
            </RightRow>
          </Right>
        </Inner>
      </Wrap>

      {isModalOpen && (
        <ModalOverlay onMouseDown={onCloseModal} aria-label="Modal - odbierz kod promocyjny">
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Odbierz kod promocyjny</ModalTitle>
              <ModalClose type="button" aria-label="Zamknij" onClick={onCloseModal}>
                ×
              </ModalClose>
            </ModalHeader>

            <ModalSub>
              Wpisz e-mail i zaznacz zgodę. <strong>Wyślemy kod</strong>.
            </ModalSub>

            <Form onSubmit={onSubmit}>
              <EmailInput
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Twój e-mail"
                type="email"
                autoComplete="email"
                disabled={loading || expired}
                aria-label="Adres e-mail"
              />

              <ConsentRow>
                <Checkbox
                  id="promo-rodo"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  disabled={loading || expired}
                />
                <ConsentLabel htmlFor="promo-rodo">
                  Wyrażam zgodę na przetwarzanie mojego adresu e-mail w celu otrzymania kodu promocyjnego
                  i komunikacji związanej z promocją (RODO).
                </ConsentLabel>
              </ConsentRow>

              {status.type !== "idle" && (
                <Status $type={status.type} role="status" aria-live="polite">
                  {status.message}
                </Status>
              )}

              <ModalActions>
                <SecondaryButton type="button" onClick={onCloseModal} disabled={loading}>
                  Anuluj
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={loading || expired}>
                  {loading ? "Wysyłanie..." : "Wyślij kod"}
                </PrimaryButton>
              </ModalActions>

              <FinePrint>Nie spamujemy. W każdej chwili możesz poprosić o usunięcie danych.</FinePrint>
            </Form>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

export default PromoBanner;

/* ---------------- API ---------------- */

async function sendDiscountCode({ email, consent }) {
  const data = await fetchJSON(`${API_BASE}/api/promo/send-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, consent }),
  });

  if (!data || data.ok !== true) {
    throw new Error((data && data.error) || "SEND_FAILED");
  }

  return true;
}

function mapErrorToMessage(code) {
  switch (code) {
    case "INVALID_EMAIL":
      return "Podaj poprawny adres e-mail.";
    case "CONSENT_REQUIRED":
      return "Aby otrzymać kod, zaznacz zgodę RODO.";
    case "SERVER_ERROR":
      return "Błąd serwera. Spróbuj ponownie za chwilę.";
    default:
      if (String(code).startsWith("HTTP ")) return "Nie udało się wysłać kodu. Spróbuj ponownie.";
      return "Nie udało się wysłać kodu. Spróbuj ponownie.";
  }
}

/* ---------------- helpers ---------------- */

function getTimeLeft(target) {
  const now = new Date();
  const diff = target.getTime() - now.getTime();
  const totalMs = diff;

  const safe = Math.max(0, diff);

  const days = Math.floor(safe / (1000 * 60 * 60 * 24));
  const hours = Math.floor((safe / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((safe / (1000 * 60)) % 60);
  const seconds = Math.floor((safe / 1000) % 60);

  return { totalMs, days, hours, minutes, seconds };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(value || "").trim());
}

/* ---------------- styles ---------------- */

const glow = keyframes`
  0% { transform: translateY(0); filter: brightness(1); }
  50% { transform: translateY(-1px); filter: brightness(1.05); }
  100% { transform: translateY(0); filter: brightness(1); }
`;

const Wrap = styled.div`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 999;
  position: relative;

  /* ✅ RADICAL COLOR - instantly visible */
  background: linear-gradient(
    90deg,
    #ff2d55 0%,
    #7c3aed 45%,
    #00d4ff 100%
  );

  border-bottom: 1px solid rgba(255, 255, 255, 0.18);

  box-shadow:
    0 10px 30px rgba(255, 45, 85, 0.28),
    0 10px 40px rgba(124, 58, 237, 0.22);

  /* glass overlay for readability */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(2, 6, 23, 0.25);
    pointer-events: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;

  width: 36px;
  height: 36px;
  border-radius: 10px;

  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(2, 6, 23, 0.55);
  color: #ffffff;

  cursor: pointer;
  font-size: 20px;
  line-height: 1;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.25);
  transition: transform 0.15s ease, filter 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.06);
    filter: brightness(1.08);
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.35);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Inner = styled.div`
  position: relative; /* ✅ above overlay */
  max-width: 1200px;
  margin: 0 auto;

  /* extra right padding so pinned X doesn't overlap right group */
  padding: 12px 60px 12px 16px;

  display: grid;
  grid-template-columns: 1.25fr auto;
  gap: 14px;
  align-items: center;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
    padding: 12px 60px 12px 16px;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;

  @media (max-width: 920px) {
    align-items: flex-start;
  }
`;

const RightRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: nowrap;
  white-space: nowrap;

  @media (max-width: 920px) {
    justify-content: flex-start;
    flex-wrap: wrap;
    white-space: normal;
  }
`;

const Title = styled.div`
  font-size: 14px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.96);

  strong {
    font-weight: 900;
    color: rgba(255, 255, 255, 0.98);
    text-shadow: 0 10px 26px rgba(0, 0, 0, 0.22);
  }

  @media (max-width: 520px) {
    font-size: 13px;
  }
`;

const Muted = styled.span`
  opacity: 0.92;
`;

const Countdown = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  white-space: nowrap;
`;

const TimeBox = styled.div`
  min-width: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 8px;
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

const TimeValue = styled.div`
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.3px;
  color: rgba(255, 255, 255, 0.96);
  line-height: 1.1;
`;

const TimeLabel = styled.div`
  font-size: 9px;
  opacity: 0.8;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.1;
`;

const Sep = styled.div`
  font-weight: 900;
  opacity: 0.75;
  color: rgba(255, 255, 255, 0.92);
  font-size: 12px;
  margin: 0 1px;
`;

const Expired = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
`;

const InlineStatus = styled.div`
  font-size: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(2, 6, 23, 0.35);
  color: rgba(255, 255, 255, 0.96);
  width: fit-content;
`;

const pulse = keyframes`
  0%   { transform: translateY(0) scale(1); box-shadow: 0 14px 38px rgba(255, 45, 85, 0.28), 0 10px 30px rgba(0, 212, 255, 0.18); }
  50%  { transform: translateY(-1px) scale(1.05); box-shadow: 0 22px 60px rgba(255, 45, 85, 0.36), 0 18px 50px rgba(124, 58, 237, 0.28), 0 14px 44px rgba(0, 212, 255, 0.22); }
  100% { transform: translateY(0) scale(1); box-shadow: 0 14px 38px rgba(255, 45, 85, 0.28), 0 10px 30px rgba(0, 212, 255, 0.18); }
`;

const GetCodeButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 900;
  cursor: pointer;

  /* 🌈 colorful CTA */
  background: linear-gradient(90deg, #ff2d55 0%, #7c3aed 45%, #00d4ff 100%);
  color: #ffffff;

  /* glow + pulse */
  animation: ${pulse} 1.6s ease-in-out infinite;
  transition: transform 0.15s ease, filter 0.15s ease, box-shadow 0.15s ease;

  /* slightly “glassy” */
  border: 1px solid rgba(255, 255, 255, 0.22);

  &:hover {
    filter: brightness(1.08) saturate(1.15);
    transform: translateY(-1px);
    animation: none; /* optional: stop pulse on hover for control */
    box-shadow: 0 26px 70px rgba(0, 0, 0, 0.28);
  }

  &:active {
    transform: translateY(0px) scale(0.99);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
    animation: none;
    box-shadow: none;
  }
`;
/* ---------------- Modal ---------------- */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 520px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.06),
    rgba(124, 58, 237, 0.18),
    rgba(0, 0, 0, 0.03)
  );
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const ModalTitle = styled.div`
  font-size: 14px;
  font-weight: 900;
  color: #0f172a;
`;

const ModalClose = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.10);
  background: rgba(255, 255, 255, 0.75);
  width: 34px;
  height: 34px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.95);
  }
`;

const ModalSub = styled.div`
  padding: 14px 16px 0 16px;
  font-size: 13px;
  line-height: 1.5;
  color: #334155;
`;

const Form = styled.form`
  padding: 14px 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.14);
  background: rgba(255, 255, 255, 0.98);
  outline: none;
  font-size: 14px;

  &:focus {
    border-color: rgba(124, 58, 237, 0.55);
    box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.14);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ConsentRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const Checkbox = styled.input`
  margin-top: 3px;
  width: 16px;
  height: 16px;
`;

const ConsentLabel = styled.label`
  font-size: 12px;
  line-height: 1.35;
  color: #475569;
`;

const Status = styled.div`
  font-size: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);

  ${({ $type }) =>
    $type === "success" &&
    `
      background: rgba(46, 204, 113, 0.10);
      border-color: rgba(46, 204, 113, 0.25);
      color: #14532d;
    `}

  ${({ $type }) =>
    $type === "error" &&
    `
      background: rgba(231, 76, 60, 0.10);
      border-color: rgba(231, 76, 60, 0.25);
      color: #7f1d1d;
    `}

  ${({ $type }) =>
    $type === "info" &&
    `
      background: rgba(52, 152, 219, 0.10);
      border-color: rgba(52, 152, 219, 0.25);
      color: #0b3b63;
    `}
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 2px;

  @media (max-width: 420px) {
    flex-direction: column;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 14px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  font-weight: 800;

  &:hover {
    background: rgba(255, 255, 255, 1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 14px;
  padding: 12px 14px;
  background: #7c3aed;
  color: #ffffff;
  cursor: pointer;
  font-weight: 900;

  &:hover {
    background: #0b0f19;
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FinePrint = styled.div`
  font-size: 11px;
  opacity: 0.65;
  color: #475569;
`;
